const config = document.querySelector('script[data-design-performance-config]');
const proxyUrl = config?.dataset.proxyUrl;
const detailProductId = normalizeProductId(config?.dataset.productId);
const trackFetchCartAdds = config?.dataset.fetchCartAdds === 'true';
const IMPRESSION_DWELL_MS = 900;
const FLUSH_DELAY_MS = 1600;
const METRIC_SELECTOR = 'product-card[data-product-id], [data-ac-design-metric-product-id]';
const queued = [];
const observed = new WeakSet();
const qualifyingElements = new Set();
const impressionTimers = new Map();
const seenImpressionProducts = new Set();
let flushTimer = 0;

function normalizeProductId(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const match = String(value).match(/^(?:gid:\/\/shopify\/Product\/)?([1-9]\d{0,20})$/);
  return match?.[1] ?? null;
}

function eventId() {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function metricProductId(element) {
  if (!(element instanceof Element)) return null;
  return normalizeProductId(element.getAttribute('data-product-id') || element.getAttribute('data-ac-design-metric-product-id'));
}

function queue(type, productId) {
  if (!proxyUrl || !productId || document.visibilityState !== 'visible') return;
  queued.push({ id: eventId(), productId, type });
  scheduleFlush();
}

function queueImpression(element) {
  const productId = metricProductId(element);
  if (!productId || seenImpressionProducts.has(productId)) return;
  seenImpressionProducts.add(productId);
  impressionTimers.delete(element);
  observer.unobserve(element);
  queue('impression', productId);
}

function armImpression(element) {
  if (document.visibilityState !== 'visible' || impressionTimers.has(element)) return;
  const productId = metricProductId(element);
  if (!productId || seenImpressionProducts.has(productId)) return;
  const timer = window.setTimeout(() => queueImpression(element), IMPRESSION_DWELL_MS);
  impressionTimers.set(element, timer);
}

function disarmImpression(element) {
  const timer = impressionTimers.get(element);
  if (timer) window.clearTimeout(timer);
  impressionTimers.delete(element);
}

const observer = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    const element = entry.target;
    const isQualifying = entry.isIntersecting && entry.intersectionRatio >= 0.5;
    if (isQualifying) {
      qualifyingElements.add(element);
      armImpression(element);
    } else {
      qualifyingElements.delete(element);
      disarmImpression(element);
    }
  }
}, { threshold: [0, 0.5] });

function observeElement(element) {
  if (!(element instanceof Element) || observed.has(element) || element.hasAttribute('data-placeholder')) return;
  if (!metricProductId(element)) return;
  observed.add(element);
  observer.observe(element);
}

function observeWithin(root) {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (root instanceof Element && root.matches(METRIC_SELECTOR)) observeElement(root);
  root.querySelectorAll?.(METRIC_SELECTOR).forEach(observeElement);
}

function scheduleFlush() {
  if (flushTimer || queued.length === 0) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = 0;
    flush();
  }, FLUSH_DELAY_MS);
}

function endpoint() {
  try {
    const url = new URL(proxyUrl, window.location.origin);
    return url.origin === window.location.origin ? url.href : null;
  } catch {
    return null;
  }
}

function returnEvents(events) {
  if (events.length) queued.unshift(...events);
  scheduleFlush();
}

function flush({ keepalive = false } = {}) {
  const url = endpoint();
  if (!url || queued.length === 0) return;
  const events = queued.splice(0, 24);
  const body = JSON.stringify({ events });

  if (keepalive && navigator.sendBeacon) {
    const sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    if (!sent) returnEvents(events);
    return;
  }

  fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body,
    keepalive: true,
  }).then((response) => {
    if (!response.ok) returnEvents(events);
  }).catch(() => returnEvents(events));
}

function trackCardClick(event) {
  if (event.defaultPrevented || event.button > 0) return;
  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest('a[href]');
  if (!link) return;
  const card = link.closest(METRIC_SELECTOR);
  const productId = metricProductId(card);
  if (productId) queue('click', productId);
}

function trackAddToCart(event) {
  const data = event.detail?.data;
  if (!data || data.didError === true || !['product-form-component', 'quick-order-list'].includes(data.source)) return;
  const productId = normalizeProductId(data.productId);
  if (productId) queue('add_to_cart', productId);
}

function isCartAddRequest(input) {
  const source = input instanceof Request ? input.url : input;
  try {
    const path = new URL(source, window.location.href).pathname.replace(/\.js$/, '');
    return path === '/cart/add';
  } catch {
    return false;
  }
}

function trackCartAddResponse(response) {
  if (!response?.ok) return;
  response.clone().json().then((item) => {
    const productId = normalizeProductId(item?.product_id ?? item?.items?.[0]?.product_id);
    if (productId) queue('add_to_cart', productId);
  }).catch(() => {});
}

function instrumentFetchCartAdds() {
  if (!trackFetchCartAdds || typeof window.fetch !== 'function') return;
  const originalFetch = window.fetch;
  window.fetch = function designPerformanceFetch(input, init) {
    const response = originalFetch.call(this, input, init);
    if (isCartAddRequest(input)) response.then(trackCartAddResponse).catch(() => {});
    return response;
  };
}

function trackDetailView() {
  if (!detailProductId || document.visibilityState !== 'visible') return;
  window.setTimeout(() => {
    if (document.visibilityState === 'visible') queue('detail_view', detailProductId);
  }, IMPRESSION_DWELL_MS);
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    impressionTimers.forEach((timer) => window.clearTimeout(timer));
    impressionTimers.clear();
    return;
  }
  qualifyingElements.forEach(armImpression);
}

if (proxyUrl && 'IntersectionObserver' in window) {
  observeWithin(document);
  const content = document.querySelector('#MainContent');
  if (content) {
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) observeWithin(node);
      }
    }).observe(content, { childList: true, subtree: true });
  }
  document.addEventListener('click', trackCardClick, true);
  document.addEventListener('cart:update', trackAddToCart);
  instrumentFetchCartAdds();
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', () => flush({ keepalive: true }));
  trackDetailView();
}
